// My Garage count badge — keeps the storefront menu's `.garage-cart-count` in sync
// with the number of saved vehicles: the profile's garage (GET /api/garage) when
// signed in, else the local (localStorage) garage. Updates on the React app's
// `pf-garage-changed` event and cross-tab storage changes. No-op if the badge
// isn't on the page. No per-instance config.

import { getTopDocument } from '../shared/dom.js';
import { getAppBaseUrl } from '../shared/config.js';
import { readCookie } from '../shared/identity.js';
import { apiJson } from '../shared/api.js';

var COUNT_SELECTOR = '.garage-cart-count';
var STORAGE_KEY = 'parts-finder-garage';

function parseVehicles() {
  try {
    var raw = (window.top || window).localStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    var vehicles = parsed && parsed.state && parsed.state.vehicles;
    return Array.isArray(vehicles) ? vehicles : [];
  } catch (e) { return []; }
}
// Mirror of app/src/utils/vehicleCompleteness.ts — a vehicle "needs info" until
// it has a friendly name, VIN, a confidence level and a licence-disc scan.
function isComplete(v) {
  return !!(v && v.friendlyName && v.vin && v.confidence && v.licenseDiscImageUrl);
}
function countIncomplete(vehicles) {
  var n = 0;
  for (var i = 0; i < vehicles.length; i++) if (!isComplete(vehicles[i])) n++;
  return n;
}
function localCount() { return parseVehicles().length; }
function localIncomplete() { return countIncomplete(parseVehicles()); }

var WARN_CLASS = 'pf-garage-warn';
// A subtle orange ⚠ dropped in right after the count badge, inside the same
// "My Garage" nav link, shown when any saved vehicle still needs information.
function ensureWarnEl(countEl) {
  var parent = countEl.parentNode;
  if (!parent) return null;
  var warn = parent.querySelector('.' + WARN_CLASS);
  if (!warn) {
    warn = getTopDocument().createElement('span');
    warn.className = WARN_CLASS;
    warn.setAttribute('aria-hidden', 'true');
    warn.style.cssText = 'display:none;margin-left:4px;color:#f59e0b;font-weight:700;line-height:1;';
    warn.textContent = '⚠';
    parent.insertBefore(warn, countEl.nextSibling);
  }
  return warn;
}

// My Garage is a signed-in feature, so the nav badge (count + warning) is only
// shown when we've confirmed a logged-in profile. `_loggedIn` is null until the
// first /api/garage check (or a pf-garage-changed event carrying loggedIn)
// resolves; while unknown, and whenever logged out, the badge stays hidden and
// the leftover localStorage garage is NOT surfaced.
var _loggedIn = null;
var _lastCount = 0;
var _lastIncomplete = 0;

function render(count, incomplete) {
  var n = (typeof count === 'number' && count >= 0) ? count : 0;
  var inc = (typeof incomplete === 'number' && incomplete >= 0) ? incomplete : 0;
  _lastCount = n;
  _lastIncomplete = inc;
  var text = String(n);
  var display = n > 0 ? '' : 'none';
  var els = getTopDocument().querySelectorAll(COUNT_SELECTOR);
  for (var i = 0; i < els.length; i++) {
    var el = els[i];
    // Only write when changed — render() runs inside a subtree MutationObserver,
    // so unconditional writes would re-trigger it and spin into a CPU-pegging loop.
    if (el.textContent !== text) el.textContent = text;
    if (el.style.display !== display) el.style.display = display;
    var warn = ensureWarnEl(el);
    if (warn) {
      var wd = (inc > 0 && n > 0) ? 'inline-block' : 'none';
      var wt = inc > 0 ? (inc + ' vehicle' + (inc !== 1 ? 's' : '') + ' need more info') : '';
      if (warn.style.display !== wd) warn.style.display = wd;
      if (warn.getAttribute('title') !== wt) warn.setAttribute('title', wt);
    }
  }
}

// Gate the shown values on login state: signed out (or unknown) → 0 / hidden.
function apply(count, incomplete, loggedIn) {
  if (typeof loggedIn === 'boolean') _loggedIn = loggedIn;
  if (_loggedIn === true) {
    render(typeof count === 'number' ? count : _lastCount, typeof incomplete === 'number' ? incomplete : _lastIncomplete);
  } else {
    render(0, 0);
  }
}

function refreshFromServer() {
  if (!getAppBaseUrl()) { apply(0, 0, false); return; }
  var headers = {};
  var jwt = readCookie('commerce_features'); if (jwt) headers['X-Commerce-Features'] = jwt;
  var sess = readCookie('Session'); if (sess) headers['X-Session-Reference'] = sess;
  apiJson('/api/garage', { headers: headers }, null).then(function (data) {
    if (data && data.loggedIn && Array.isArray(data.vehicles)) apply(data.vehicles.length, countIncomplete(data.vehicles), true);
    else apply(0, 0, false);
  }).catch(function () { apply(0, 0, false); });
}

export function init() {
  try { render(0, 0); } catch (e) { /* ignore */ } // hidden until login is known
  refreshFromServer();
  var win = window.top || window;
  try {
    win.addEventListener('pf-garage-changed', function (e) {
      var d = (e && e.detail) ? e.detail : {};
      var c = typeof d.count === 'number' ? d.count : undefined;
      var inc = typeof d.incomplete === 'number' ? d.incomplete : undefined;
      var li = typeof d.loggedIn === 'boolean' ? d.loggedIn : undefined;
      apply(c, inc, li);
    });
  } catch (e) { /* ignore */ }
  try {
    win.addEventListener('storage', function (e) {
      // Only re-read the local cache when signed in (it mirrors the profile then);
      // logged out, keep the badge hidden.
      if (!e || e.key === STORAGE_KEY || e.key === null) apply(localCount(), localIncomplete(), _loggedIn);
    });
  } catch (e) { /* ignore */ }
  try {
    var doc = getTopDocument();
    var raf = (win.requestAnimationFrame || function (cb) { return setTimeout(cb, 100); }).bind(win);
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      raf(function () {
        scheduled = false;
        if (doc.querySelector(COUNT_SELECTOR)) apply(_lastCount, _lastIncomplete, _loggedIn);
      });
    }).observe(doc.documentElement || doc.body, { childList: true, subtree: true });
  } catch (e) { /* ignore */ }
}

export var id = 'garage-count';
